/**
 * RetentionChips - the retention of a customer in the header (P6-S7, Stefan 7.14 / 10.4, Kap. 8 Legal Hold):
 * «Aufbewahrung bis dd.mm.yyyy» once the nightly run set it, «Legal Hold» with the reason as tooltip, and
 * for tenant.manage the hold actions (set on a gekündigt / inaktiv customer, lift in any status). A declined
 * Löschvorschlag shows «Löschvorschlag abgelehnt» and, for tenant.manage on an inaktiv customer without a hold,
 * «Löschvorschlag erneut» (OQ-S7-7: final until then); an approved one «Löschung freigegeben». The API
 * decides; the screen only renders the read model and refreshes it after a change. A refusal shows the api's
 * own sentence (retentionErrorText), not a generic one.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RETENTION_REASON_MAX, retentionErrorText, retentionUntilLabel } from '@brokly/shared';
import type { CustomerRetentionDto } from '@brokly/shared';
import { Badge } from '../../../components/ui/Badge';
import { Button, Dialog } from '../../administration/components';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';

const HOLD_STATUSES = new Set(['gekuendigt', 'inaktiv']);

export function RetentionChips({
  subjectType,
  subjectId,
  status,
  retention,
  canManage,
}: {
  subjectType: 'person' | 'firma';
  subjectId: string;
  status: string;
  retention: CustomerRetentionDto;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: qk.customerStatusOverlay(subjectType, subjectId) });

  const setHold = useMutation({
    mutationFn: () => apiClient.retention.setLegalHold(subjectType, subjectId, reason.trim()),
    onSuccess: () => {
      setOpen(false);
      setReason('');
      refresh();
    },
  });
  const liftHold = useMutation({
    mutationFn: () => apiClient.retention.liftLegalHold(subjectType, subjectId),
    onSuccess: refresh,
  });

  const repropose = useMutation({
    mutationFn: () => apiClient.retention.repropose(subjectType, subjectId),
    onSuccess: refresh,
  });

  const canSet = canManage && !retention.legalHold && HOLD_STATUSES.has(status);
  const declined = retention.deletionDeclinedAt !== null;
  const canRepropose = canManage && declined && status === 'inaktiv' && !retention.legalHold && retention.deletionApprovedAt === null;
  const canLift = canManage && retention.legalHold;

  return (
    <>
      {retention.until !== null && (
        <Badge variant="neutral" data-testid="retention-chip">
          {retentionUntilLabel(retention.until)}
        </Badge>
      )}
      {retention.legalHold && (
        <Badge variant="alert" data-testid="legal-hold-chip" title={retention.legalHoldReason ?? undefined}>
          Legal Hold
        </Badge>
      )}
      {declined && (
        <Badge variant="neutral" data-testid="deletion-declined-chip">
          Löschvorschlag abgelehnt
        </Badge>
      )}
      {retention.deletionApprovedAt !== null && (
        <Badge variant="alert" data-testid="deletion-approved-chip">
          Löschung freigegeben
        </Badge>
      )}
      {canRepropose && (
        <Button variant="ghost" size="sm" disabled={repropose.isPending} onClick={() => repropose.mutate()}>
          Löschvorschlag erneut
        </Button>
      )}
      {canSet && (
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
          Legal Hold setzen
        </Button>
      )}
      {canLift && (
        <Button variant="ghost" size="sm" disabled={liftHold.isPending} onClick={() => liftHold.mutate()}>
          Legal Hold aufheben
        </Button>
      )}
      {(repropose.isError || liftHold.isError) && (
        <span className="text-sm text-status-error" data-testid="retention-action-error">
          {retentionErrorText(repropose.error ?? liftHold.error, 'Die Aktion konnte nicht ausgeführt werden.')}
        </span>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Legal Hold setzen"
        description="Solange ein Legal Hold besteht, wird kein Löschvorschlag erstellt und keine Löschung freigegeben."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button disabled={reason.trim().length === 0 || setHold.isPending} onClick={() => setHold.mutate()}>
              Speichern
            </Button>
          </>
        }
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Grund</span>
          <textarea
            aria-label="Grund"
            className="min-h-24 rounded-md border border-border bg-background p-2 text-sm"
            maxLength={RETENTION_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        {setHold.isError && <p className="mt-2 text-sm text-status-error">{retentionErrorText(setHold.error, 'Der Legal Hold konnte nicht gesetzt werden.')}</p>}
      </Dialog>
    </>
  );
}
